import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { GameFeature as GameFeatureEnum } from '@/models/games/enums/GameFeature';
import { GameType } from '@/models/games/enums/GameType';
import {
    ROUTE_FAVORITE,
    ROUTE_FEATURED,
    ROUTE_LOBBY,
    ROUTE_MULTIBET,
} from '@/models/Route';
import { getMainPlayerState } from '@/modules/main/selector';
import { isBitActive } from '@/utils/bitwiseUtil';
import { calcLowHigh } from '@/utils/commonFunc';
import { GA_EVENT_CATEGORY, getNavTab, sendGA } from '@/utils/ga';
// import Box from '@mui/material/Box';
import Box from '@mui/material/Box';
import Grid, { GridSize } from '@mui/material/Grid';
import { clsx } from 'clsx';
import { ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import {
    FavoriteIcon,
    FeaturedIcon,
    LiveGamesIcon,
    MultiBetIcon,
} from '../../icon';
import { getLobbyStateHallGameTypeMap } from '../../selector';
import { ExpandButton } from './ExpandButton';
import './index.scss';

export const NavBar = () => {
    const { isLandscape } = useContext(ScreenContext);
    const { GameFeature } = useSelector(getMainPlayerState);
    const { hideMultiBet } = useContext(CommonConfigContext);
    const hallGameTypeMap = useSelector(getLobbyStateHallGameTypeMap);

    const isShowFeature = useMemo(() => {
        if (GameFeature) {
            const curGameFeature = calcLowHigh(GameFeature);
            return isBitActive(curGameFeature, GameFeatureEnum.FeatureTab);
        } else {
            return false;
        }
    }, [GameFeature]);

    const isShowMultibet = useMemo(() => {
        if (
            hallGameTypeMap.findIndex(v => v.gameType == GameType.Baccarat) < 0
        ) {
            return false;
        } else {
            return !hideMultiBet;
        }
    }, [hideMultiBet, hallGameTypeMap]);

    const grid = useMemo(() => {
        // feature, favorite, live game, multi bet
        let navNum = 4;
        if (!isShowFeature) navNum--;
        if (!isShowMultibet) navNum--;
        const portraitGrid = 12 / navNum;
        return isLandscape ? 1 : portraitGrid;
    }, [isLandscape, isShowFeature, isShowMultibet]);
    return (
        <Grid
            container
            className="lobby-nav-bar"
            direction={isLandscape ? 'column' : 'row'}
        >
            {isShowFeature && (
                <NavCell
                    grid={grid}
                    isLandscape={isLandscape}
                    to={ROUTE_FEATURED}
                    icon={<FeaturedIcon />}
                    title={'nav.feature'}
                />
            )}

            <NavCell
                grid={grid}
                isLandscape={isLandscape}
                to={ROUTE_FAVORITE}
                icon={<FavoriteIcon />}
                title={'nav.favorite'}
            />
            <NavCell
                grid={grid}
                isLandscape={isLandscape}
                to={ROUTE_LOBBY}
                icon={<LiveGamesIcon />}
                title={'nav.live_games'}
            />
            {isShowMultibet && (
                <NavCell
                    grid={grid}
                    isLandscape={isLandscape}
                    to={ROUTE_MULTIBET}
                    icon={<MultiBetIcon />}
                    title={'nav.multibet'}
                />
            )}
            <Box className="expand-button-container">
                <ExpandButton />
            </Box>
        </Grid>
    );
};
type NavCellProps = {
    grid?: boolean | GridSize;
    isLandscape: boolean;
    to: string;
    icon: ReactNode;
    title: string;
};
const NavCell = (props: NavCellProps) => {
    const { grid, isLandscape, to, icon, title } = props;
    const { playButtonAudio } = useContext(AudioPlayContext);

    const { t } = useTranslation();
    return (
        <Grid item xs={grid} className="nav-cell-container">
            <NavLink
                to={to}
                onClick={() => {
                    playButtonAudio();
                    const currentPage = getNavTab();
                    const eventName = getNavTab(to);
                    sendGA(
                        eventName,
                        GA_EVENT_CATEGORY.ENGAGEMENT,
                        currentPage
                    );
                }}
                className={({ isActive }) => (isActive ? 'active' : '')}
            >
                {({ isActive }) => (
                    <div
                        className={`nav-cell-item-${isLandscape ? 'row' : 'column'}`}
                    >
                        <div className={clsx('icon', isActive && 'active')}>
                            {icon}
                        </div>
                        <div
                            className={`nav-text my-font-12 ${isActive ? 'active' : ''}`}
                        >
                            {t(title)}
                        </div>
                    </div>
                )}
            </NavLink>
        </Grid>
    );
};
